import React, { useState, useEffect } from "react";
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import { useRentalInvoiceEditContext } from "./RentalInvoiceEditContainer";
import { documentNoun } from "./statusLabels";

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  return formatter.format(value);
};

const totalColumnStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const totalRowStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
};

const inputWrapperStyle = {
  border: "1px solid var(--sl-border-secondary, #ececec)",
  borderRadius: "8px",
  width: "fit-content",
};

const RentalInvoiceTotalsCard = () => {
  const {
    rentalInvoice,
    rentalInvoiceFormState,
    setRentalInvoiceFormState,
  } = useRentalInvoiceEditContext();

  const [discount, setDiscount] = useState(rentalInvoice.discount || 0);

  useEffect(() => {
    setRentalInvoiceFormState({ ...rentalInvoiceFormState, discount: discount });
  }, [discount]);

  return (
    <div className="card">
      <div className="card-body">
        <div className="setlist-text-lg setlist-font-semibold figma-text secondary" style={{marginBottom: "10px"}}>Totals</div>
        <div className="d-flex flex-wrap" style={{gap: "20px 64px"}}>
          <div className="flex-grow-1" style={{...totalColumnStyle, minWidth: "300px"}}>
            <div className="setlist-text-md setlist-font-semibold figma-text secondary">Customer {documentNoun(rentalInvoiceFormState.status)}</div>
            <div className="setlist-text-md" style={totalRowStyle}>
              <div>Subtotal</div>
              <div>{formatCurrency(rentalInvoice.subtotal)}</div>
            </div>
            <div className="setlist-text-md" style={totalRowStyle}>
              <div>Discount</div>
              <div className="input-group inline-white" style={inputWrapperStyle}>
                <div className="input-group-prepend">
                  <span className="input-group-text px-2">
                    <i className="far fa-dollar-sign text-muted"></i>
                  </span>
                </div>
                <MaskedInput
                  id="discount"
                  className="form-control"
                  placeholder={"0.00"}
                  mask={createNumberMask({
                    prefix: "",
                    allowDecimal: true,
                    decimalLimit: 2,
                    allowNegative: false,
                    includeThousandsSeparator: false
                  })}
                  value={discount}
                  style={{
                    width: 100,
                    textAlign: "right",
                    paddingRight: 0,
                  }}
                  onFocus={e => e.target.select()}
                  onChange={e => { setDiscount(e.target.value) }}
                />
              </div>
            </div>
            {(rentalInvoice.rental_invoice_tax_fees || []).map((taxFee) => (
              <div className="setlist-text-md" style={totalRowStyle} key={taxFee.id}>
                <div>{taxFee.name || "Tax/Fee"} ({parseFloat(taxFee.rate)}%)</div>
                <div>{formatCurrency(taxFee.calculated_amount)}</div>
              </div>
            ))}
            <hr />
            <div className="setlist-text-md setlist-font-semibold" style={totalRowStyle}>
              <div>Total</div>
              <div>{formatCurrency(rentalInvoice.grand_total ?? (rentalInvoice.subtotal - (rentalInvoice.discount || 0)))}</div>
            </div>
            <div className="setlist-text-md" style={totalRowStyle}>
              <div>Deposit Paid</div>
              <div>{formatCurrency(rentalInvoice.deposit_paid_amount)}</div>
            </div>
            <div className="setlist-text-md" style={totalRowStyle}>
              <div>Remaining Balance</div>
              <div>{formatCurrency(rentalInvoice.remaining_balance)}</div>
            </div>
          </div>
          <div className="flex-grow-1" style={{...totalColumnStyle, minWidth: "300px", padding: "0 0 0 0"}}>
            <div className="setlist-text-md setlist-font-semibold figma-text secondary">Actual Cost</div>
            <div className="setlist-text-md" style={totalRowStyle}>
              <div>Total Actual Cost</div>
              <div>{formatCurrency(rentalInvoice.total_actual_cost)}</div>
            </div>
            <div className="setlist-text-md" style={totalRowStyle}>
              <div>Revenue</div>
              <div>{formatCurrency(rentalInvoice.revenue)}</div>
            </div>
            <hr />
            <div className="setlist-text-md" style={totalRowStyle}>
              <div>Profit Margin</div>
              <div>{rentalInvoice.profit_margin}%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalInvoiceTotalsCard;
